/**
 * Single source of truth for the pickup time slot grid (admin app).
 *
 * To move every pickup dropdown to a different step (10, 15, 20, 30 ...) change
 * PICKUP_SLOT_INTERVAL_MINUTES only. The value must divide 60 evenly so that every
 * hour boundary stays on the grid.
 *
 * The backoffice app keeps its own copy of this file at `v1/data/pickupTime.js`
 * (the two Vue apps are built separately and share no module path). The backend
 * counterpart is `TimeHelper::PICKUP_SLOT_INTERVAL_MINUTES`. Keep all three in sync.
 */
export const PICKUP_SLOT_INTERVAL_MINUTES = 15;

/** Booking popup grid bounds. The end bound is inclusive-by-interval: the last generated */
/** slot is the last one that fits before midnight (23:45 at 15 min, 23:30 at 30 min). */
export const PICKUP_GRID_START = '06:00';
export const PICKUP_GRID_END = '23:59';

/** Location pickup-window settings grid bounds (settings/locations.vue). */
export const PICKUP_WINDOW_GRID_START = '06:00';
export const PICKUP_WINDOW_GRID_END = '20:00';

const MINUTES_PER_DAY = 24 * 60;

/**
 * "HH:MM" -> minutes since midnight. Returns null for anything unparsable.
 * @param {string} time
 * @returns {number|null}
 */
export function timeToMinutes(time) {
  if (typeof time !== 'string') {
    return null;
  }

  const match = time.match(/^(\d{1,2}):(\d{2})/);
  if (!match) {
    return null;
  }

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours > 23 || minutes > 59) {
    return null;
  }

  return (hours * 60) + minutes;
}

/**
 * Minutes since midnight -> "HH:MM". Wraps around the day, so 1440 becomes "00:00".
 * @param {number} minutes
 * @returns {string}
 */
export function minutesToTime(minutes) {
  const normalized = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalized / 60);
  const mins = normalized % 60;

  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Round a time up to the next slot on the grid. A time already on the grid is left
 * untouched. Rounding past the end of the day wraps to "00:00", which is what the
 * booking popups relied on before (23:xx -> 00:00).
 * @param {string} time
 * @param {number} [interval]
 * @returns {string} the original input when it cannot be parsed
 */
export function roundUpToInterval(time, interval = PICKUP_SLOT_INTERVAL_MINUTES) {
  const minutes = timeToMinutes(time);
  if (minutes === null) {
    return time;
  }

  return minutesToTime(Math.ceil(minutes / interval) * interval);
}

/**
 * Round a time down to the previous slot on the grid. A time already on the grid is
 * left untouched.
 * @param {string} time
 * @param {number} [interval]
 * @returns {string} the original input when it cannot be parsed
 */
export function roundDownToInterval(time, interval = PICKUP_SLOT_INTERVAL_MINUTES) {
  const minutes = timeToMinutes(time);
  if (minutes === null) {
    return time;
  }

  return minutesToTime(Math.floor(minutes / interval) * interval);
}

/**
 * Every slot between `from` and `to` (both inclusive) that sits on the grid.
 * @param {string} from
 * @param {string} to
 * @param {number} [interval]
 * @returns {string[]}
 */
export function generatePickupSlots(from, to, interval = PICKUP_SLOT_INTERVAL_MINUTES) {
  const fromMinutes = timeToMinutes(from);
  const toMinutes = timeToMinutes(to);
  if (fromMinutes === null || toMinutes === null || fromMinutes > toMinutes) {
    return [];
  }

  const slots = [];
  const start = Math.ceil(fromMinutes / interval) * interval;
  for (let minutes = start; minutes <= toMinutes; minutes += interval) {
    slots.push(minutesToTime(minutes));
  }

  return slots;
}

/**
 * The slots of the grid that fall inside [from, to].
 *
 * Endpoints do not have to sit on the grid: `from` is rounded up and `to` is rounded
 * down, so a carrier close time of 17:20 simply yields 17:15 as the last option instead
 * of dropping the whole range. Returns an empty array when the window holds no slot.
 * @param {string} from
 * @param {string} to
 * @param {string[]} grid
 * @returns {string[]}
 */
export function slotsWithinRange(from, to, grid) {
  const fromMinutes = timeToMinutes(from);
  const toMinutes = timeToMinutes(to);
  if (fromMinutes === null || toMinutes === null || fromMinutes > toMinutes) {
    return [];
  }

  return grid.filter((slot) => {
    const slotMinutes = timeToMinutes(slot);

    return slotMinutes !== null && slotMinutes >= fromMinutes && slotMinutes <= toMinutes;
  });
}
